import { useEffect, useState } from "react";
import { Banknote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/dash";
import StatusBadge from "@/components/StatusBadge";
import CoinIcon from "@/components/CoinIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { formatCrypto, formatDate, truncate } from "@/lib/format";

const NETWORKS = {
  BTC: [["BTC", "Bitcoin"]],
  ETH: [["ERC20", "Ethereum (ERC20)"]],
  USDT: [["TRC20", "Tron (TRC20)"], ["ERC20", "Ethereum (ERC20)"]],
  USDC: [["ERC20", "Ethereum (ERC20)"], ["SOL", "Solana"]],
  SOL: [["SOL", "Solana"]],
};

export default function Payout() {
  const [balances, setBalances] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [form, setForm] = useState({ coin: "USDT", network: "TRC20", amount: "", address: "", totp: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/wallet").then(({ data }) => setBalances(data.balances)).catch(() => setBalances([]));
    api.get("/payouts").then(({ data }) => setPayouts(data.payouts)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const bal = balances?.find((b) => b.coin === form.coin);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payouts", { ...form, amount: parseFloat(form.amount) });
      toast.success("Para çekme talebi oluşturuldu.");
      setForm((f) => ({ ...f, amount: "", address: "", totp: "" }));
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  if (!balances) return <Spinner />;

  return (
    <div>
      <PageHeader title="Para Çekme" desc="Kripto bakiyenizi kendi cüzdanınıza güvenle çekin." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-base font-bold mb-4">Yeni Para Çekme</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="payout-form">
            <div>
              <Label className="text-sm">Coin</Label>
              <Select value={form.coin} onValueChange={(v) => setForm((f) => ({ ...f, coin: v, network: NETWORKS[v][0][0] }))}>
                <SelectTrigger className="mt-1.5" data-testid="payout-coin"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(NETWORKS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Network</Label>
              <Select value={form.network} onValueChange={(v) => setForm((f) => ({ ...f, network: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="payout-network"><SelectValue /></SelectTrigger>
                <SelectContent>{NETWORKS[form.coin].map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex justify-between"><Label className="text-sm">Çekilecek Tutar</Label><span className="text-xs text-muted-foreground">Bakiye: {formatCrypto(bal?.available || 0, form.coin)}</span></div>
              <Input required type="number" step="0.00000001" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="payout-amount" className="mt-1.5" placeholder="0.00" />
            </div>
            <div><Label className="text-sm">Alıcı Cüzdan Adresi</Label><Input required value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="payout-address" className="mt-1.5 font-mono text-xs" placeholder="Cüzdan adresi" /></div>
            <div><Label className="text-sm">2FA Kodu (etkinse)</Label><Input value={form.totp} onChange={(e) => setForm((f) => ({ ...f, totp: e.target.value }))} className="mt-1.5" placeholder="000000" /></div>
            <p className="text-xs text-muted-foreground">İşlem ücreti: %0.5. Ağ ve adres bilgilerini kontrol edin, blockchain işlemleri geri alınamaz.</p>
            <Button type="submit" disabled={saving} data-testid="payout-submit" className="w-full rounded-full h-11">{saving ? <Loader2 className="animate-spin" size={16} /> : "Para Çek"}</Button>
          </form>
        </Card>

        <Card className="p-6">
          <h3 className="text-base font-bold mb-4">Para Çekme Geçmişi</h3>
          {payouts.length === 0 ? (
            <EmptyState icon={Banknote} title="Henüz para çekme yok" />
          ) : (
            <div className="space-y-2" data-testid="payouts-list">
              {payouts.map((p) => (
                <div key={p.id} className="rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 font-semibold"><CoinIcon symbol={p.coin} size={20} /> {formatCrypto(p.amount, p.coin)}</span>
                    <StatusBadge status={p.status} kind="payout" />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                    <p>Ağ: {p.network} · Ücret: {formatCrypto(p.fee, p.coin)}</p>
                    <p>Adres: {truncate(p.address, 10)}</p>
                    {p.tx_hash && <p>Hash: {truncate(p.tx_hash, 8)}</p>}
                    <p>{formatDate(p.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
